import * as React from 'react';
import { Toggle } from './ui/Toggle';
import { Modal } from './ui/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'controller/types';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { useCommunicationSetupSlice } from 'features/CommunicationSetup/slice';
import { warning } from 'core/components/Notify';

import { SOCIAL_MEDIA } from 'features/SocialHub/components/DesktopComponents/ListDetails/SocialHubDashboard/constants';
import { useSocialMediaSelection } from 'features/SocialHub/components/DesktopComponents/ListDetails/SocialHubDashboard/hooks/useSocialMediaSelection';
import { useSocialHubStatus } from 'features/SocialHub/components/DesktopComponents/ListDetails/SocialHubDashboard/hooks/useSocialHubStatus';
import { useSocialAuth } from 'features/SocialHub/components/DesktopComponents/ListDetails/SocialHubDashboard/hooks/useSocialAuth';
import { useSocialHubEvents } from 'features/SocialHub/components/DesktopComponents/ListDetails/SocialHubDashboard/hooks/useSocialHubEvents';
import { AccountSelectionList } from 'features/SocialHub/components/DesktopComponents/ListDetails/SocialHubDashboard/components/AccountSelectionList';
import { ReactComponent as LoadingSpinnerFull } from 'assets/images/DigitalMarketing/loading-spinner-full.svg';
import { DMButton, DMDiv, DMText, DMBr } from './Primitives';

export const SocialMediaCard = () => {
  const dispatch = useDispatch();
  const authData = useAuth();
  const { actions: commActions } = useCommunicationSetupSlice();

  const commState: any = useSelector(
    (state: RootState) => state.communicationSetup
  );

  const [triggerConnect, setTriggerConnect] = React.useState<boolean>(false);

  const { selectedSocialMedia, selectSocialMedia } = useSocialMediaSelection();
  const { socialHubStatus, loader } = useSocialHubStatus(
    commState?.isUpsertLoading
  );
  const { connect, whatsAppSession } = useSocialAuth(selectedSocialMedia);
  useSocialHubEvents();

  React.useEffect(() => {
    if (triggerConnect && selectedSocialMedia) {
      setTriggerConnect(false);
      connect();
    }
  }, [triggerConnect, selectedSocialMedia, connect]);

  const isWhatsApp = selectedSocialMedia?.NAME === 'WhatsApp Business';
  const isWhatsAppConnected =
    isWhatsApp && socialHubStatus?.[selectedSocialMedia?.id]?.status;
  const hasList =
    !!commState?.listResponse?.length ||
    !!commState?.listResponse?.list?.length;
  const hasToken =
    !!commState?.ACCESS_TOKEN ||
    !!commState?.listResponse?.access_token ||
    isWhatsApp;

  const showList = hasList && hasToken && !isWhatsAppConnected;
  const listData =
    commState?.listResponse?.list ||
    commState?.listResponse?.data ||
    commState?.listResponse;

  const isProcessing = triggerConnect || commState?.isUpsertLoading;
  const isStatusLoading = loader && !isProcessing;

  const handleToggle = (platformId: string, currentStatus: boolean) => {
    if (currentStatus) {
      warning(
        <>
          Please create a support ticket to disconnect this profile.
          <DMBr />
          <DMButton
            type="button"
            className="text-primary-main hover:underline font-semibold mt-1 inline-block"
          >
            Create Support Ticket
          </DMButton>
        </>,
        true
      );
      return;
    }
    const selected = SOCIAL_MEDIA.find(item => item.id === platformId);
    if (selected) {
      selectSocialMedia(selected);
      setTriggerConnect(true);
    }
  };

  const handleSelection = (item: any) => {
    if (selectedSocialMedia?.NAME === 'WhatsApp Business') {
      dispatch(
        commActions.createCommunicationSetupStart({
          URL: 'whatsapp/selectBusiness',
          data: {
            INSTITUTE_ID: authData?.INSTITUTE_ID || 0,
            BRANCH_ID: authData?.BRANCH_ID || 0,
            WA_PH_ID: whatsAppSession?.phoneNumberId || '',
            BUSINESS_ID: whatsAppSession?.wabaId || '',
            CODE: whatsAppSession?.code || '',
          },
          message: 'Selected Successfully !',
        })
      );
    } else if (
      ['Google Business', 'Youtube'].includes(selectedSocialMedia?.NAME)
    ) {
      dispatch(
        commActions.createCommunicationSetupStart({
          URL: 'google/update_info',
          data: {
            BRANCH_ID: authData?.BRANCH_ID,
            ACCESS_TOKEN: commState?.listResponse?.access_token,
            REFRESH_TOKEN: commState?.listResponse?.refresh_token,
            PREFERRED_ID: item?.id || item?.name,
            SERVICE: selectedSocialMedia?.id,
          },
          message: 'Selected Successfully !',
        })
      );
    } else {
      dispatch(
        commActions.createCommunicationSetupStart({
          URL: 'facebook/update_info',
          data: {
            BRANCH_ID: authData?.BRANCH_ID,
            ACCESS_TOKEN: item?.access_token || commState?.ACCESS_TOKEN,
            PAGE_ID: item?.id || item?.fb_page_id,
            SERVICE: selectedSocialMedia?.id,
          },
          message: 'Selected Successfully !',
        })
      );
    }
    dispatch(commActions.clearSocialHubData());
  };

  const platforms = [
    { name: 'Facebook', id: 'fb' },
    { name: 'Instagram', id: 'ig' },
    { name: 'Google Business', id: 'gb' },
    { name: 'WhatsApp', id: 'wa' },
    { name: 'YouTube', id: 'yt' },
  ];

  return (
    <DMDiv className="bg-white rounded-[2vh] border border-gray-200 pt-[1.5vh] px-[1.5vh] pb-[0.5vh] shadow-sm h-full flex flex-col">
      {/* Header */}
      <DMDiv className="flex items-center justify-between mb-[1vh] shrink-0">
        <DMDiv>
          <DMText
            as="h3"
            className="text-[1.8vh] font-bold text-gray-800 leading-tight shrink-0"
          >
            Status of Integrations
          </DMText>
        </DMDiv>
        {/* Modern Loader */}
        {isProcessing && (
          <DMDiv className="flex items-center gap-2 px-3 py-1 bg-primary-main rounded-full border border-primary-80 shadow-sm animate-pulse">
            <LoadingSpinnerFull
              className="animate-spin h-[1.6vh] w-[1.6vh] text-white"
              aria-hidden="true"
            />
            <DMText as="span" className="text-[1.3vh] font-bold text-white">
              Connecting...
            </DMText>
          </DMDiv>
        )}
      </DMDiv>

      {/* Platform list – single column */}
      <DMDiv className="flex flex-col gap-[1.4vh] overflow-auto mt-[0.2vh]">
        {isStatusLoading
          ? Array.from({ length: platforms.length }).map((_, index) => (
              <DMDiv
                key={`integration-skeleton-${index}`}
                className="flex animate-pulse items-center justify-between gap-[1vh] py-[0.5vh]"
              >
                <DMDiv className="flex min-w-0 flex-1 items-center gap-[0.8vh]">
                  <DMDiv className="h-[3.25vh] w-[3.25vh] shrink-0 rounded-[0.6vh] bg-gray-200" />
                  <DMDiv className="min-w-0 flex-1">
                    <DMDiv className="h-[1.55vh] w-[75%] rounded bg-gray-200" />
                    <DMDiv className="mt-[0.45vh] h-[1.2vh] w-[42%] rounded bg-gray-100" />
                  </DMDiv>
                </DMDiv>
                <DMDiv className="h-[2.4vh] w-[4.4vh] shrink-0 rounded-[2vh] bg-gray-200" />
              </DMDiv>
            ))
          : platforms.map((platform, i) => {
              const isConnected = !!socialHubStatus?.[platform.id]?.status;
              const iconElement = SOCIAL_MEDIA.find(
                item => item.id === platform.id
              )?.activeIcon as React.ReactElement;

              return (
                <DMDiv
                  key={i}
                  className="flex items-center justify-between gap-[1vh] py-[0.5vh]"
                >
                  {/* Icon + Name / Status */}
                  <DMDiv className="flex min-w-0 flex-1 items-center gap-[0.8vh]">
                    <DMDiv className="flex items-center justify-center shrink-0">
                      {iconElement
                        ? React.cloneElement(iconElement, {
                            className: iconElement.props.className.replace(
                              'text-[30px]',
                              'text-[3.25vh]'
                            ),
                          })
                        : null}
                    </DMDiv>
                    <DMDiv className="min-w-0 flex-1">
                      <DMText
                        className="truncate text-[1.55vh] font-bold leading-tight text-gray-900"
                        title={
                          platform.id === 'yt' &&
                          socialHubStatus?.[platform.id]?.info?.account_id
                            ? socialHubStatus?.[platform.id]?.info?.account_id
                            : socialHubStatus?.[platform.id]?.info?.name ||
                              platform.name
                        }
                      >
                        {platform.id === 'yt' &&
                        socialHubStatus?.[platform.id]?.info?.account_id
                          ? socialHubStatus?.[platform.id]?.info?.account_id
                          : socialHubStatus?.[platform.id]?.info?.name ||
                            platform.name}
                      </DMText>
                      <DMText
                        className={`text-[1.3vh] leading-tight ${
                          isConnected ? 'text-green-500' : 'text-gray-400'
                        }`}
                      >
                        {isConnected ? 'Connected' : 'Not Connected'}
                      </DMText>
                    </DMDiv>
                  </DMDiv>

                  {/* Toggle */}
                  <DMDiv className="shrink-0">
                    <Toggle
                      checked={isConnected}
                      onChange={() => handleToggle(platform.id, isConnected)}
                    />
                  </DMDiv>
                </DMDiv>
              );
            })}
      </DMDiv>

      {/* Account Selection Modal */}
      <Modal
        isOpen={showList}
        onClose={() => dispatch(commActions.clearSocialHubData())}
      >
        <AccountSelectionList
          list={listData}
          selectedSocialMedia={selectedSocialMedia}
          onSelect={handleSelection}
          onCancel={() => dispatch(commActions.clearSocialHubData())}
        />
      </Modal>
    </DMDiv>
  );
};

